import { useState, useEffect, useCallback } from 'react';
import { idem } from '@/utils/api';
import { useLiveRefresh } from '@/contexts/WebSocketContext';
import { isAdmin } from '@/utils/roles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { 
  CreditCard, 
  DollarSign, 
  Banknote,
  Receipt,
  LogOut,
  UserCog,
  RefreshCw,
  CheckCircle2,
  Clock,
  Wallet,
  Send,
  MessageSquare,
  Users,
  TrendingUp,
  ArrowUpRight,
  Plus,
  Minus,
  ShoppingCart,
  X,
  User,
  Shield,
  Trash2,
  ArrowRightLeft,
  Percent,
  Gift,
  ArrowLeft,
  Printer,
  UserCheck,
  Split,
  Link,
  Copy,
  Share2,
  ExternalLink,
  FileText,
  QrCode,
  Search,
  Loader2,
  Building2,
  AlertCircle,
  Smartphone
} from 'lucide-react';
import axios from 'axios';
import ModernCashier from '@/modern/staff/ModernCashier';
import { useIsModern } from '@/modern/shared';
import { toast } from 'sonner';
import { useWebSocket } from '../contexts/WebSocketContext';
import ComensalesSplitDialog from '../components/ComensalesSplitDialog';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

function CashierView() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalPending: 0, sessionCount: 0 });
  
  // Dialog states
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [checkoutTip, setCheckoutTip] = useState(0);
  const [tipPercentage, setTipPercentage] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [whatsAppPhone, setWhatsAppPhone] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Discount/courtesy states
  const [manualDiscount, setManualDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('fixed'); // 'fixed' or 'percentage'
  const [discountReason, setDiscountReason] = useState('');

  // Add items dialog state
  const [showAddItemsDialog, setShowAddItemsDialog] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [orderNotes, setOrderNotes] = useState('');
  const [submittingOrder, setSubmittingOrder] = useState(false);

  // Move item dialog state
  const [showMoveItemDialog, setShowMoveItemDialog] = useState(false);
  const [itemToMove, setItemToMove] = useState(null); // { orderId, itemIndex, item }
  
  // Waiter assignment and printing
  const [waiters, setWaiters] = useState([]);
  const [selectedWaiter, setSelectedWaiter] = useState('');
  const [printingTicket, setPrintingTicket] = useState(false);
  const [showTicketPreview, setShowTicketPreview] = useState(false);
  const [ticketHtml, setTicketHtml] = useState('');
  
  // Split bill states
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [splitType, setSplitType] = useState('equal'); // 'equal', 'by_percentage', 'by_items'
  const [splitParts, setSplitParts] = useState(2);
  const [splitPercentages, setSplitPercentages] = useState([{ percentage: 50, person_name: 'Parte 1' }, { percentage: 50, person_name: 'Parte 2' }]);
  const [creatingSplit, setCreatingSplit] = useState(false);
  const [sessionSplits, setSessionSplits] = useState(null);
  const [showSplitsView, setShowSplitsView] = useState(false);
  
  // Split por comensales (avanzado)
  const [showComensalesSplit, setShowComensalesSplit] = useState(false);
  
  // WebSocket para notificaciones
  const { lastMessage } = useWebSocket();

  // AFIP Invoice states
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [invoiceDocTipo, setInvoiceDocTipo] = useState('cf'); // cf, cuit, dni
  const [invoiceDocNro, setInvoiceDocNro] = useState('');
  const [invoiceCondicionIva, setInvoiceCondicionIva] = useState('consumidor_final');
  const [invoiceRazonSocial, setInvoiceRazonSocial] = useState('');
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [searchingTaxpayer, setSearchingTaxpayer] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [showInvoiceResult, setShowInvoiceResult] = useState(false);
  const [afipStatus, setAfipStatus] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [invoiceItemsMode, setInvoiceItemsMode] = useState('detailed'); // 'detailed', 'simple', 'custom'
  
  // Closed sessions for invoicing
  const [closedSessions, setClosedSessions] = useState([]);
  const [showClosedSessionsDialog, setShowClosedSessionsDialog] = useState(false);
  const [selectedClosedSession, setSelectedClosedSession] = useState(null);
  const [invoicingClosedSession, setInvoicingClosedSession] = useState(false);

  const fetchSessions = useCallback(async () => {
    try {
      const [dashboardRes, menuRes, staffRes] = await Promise.all([
        axios.get(`${API}/pos/dashboard`),
        axios.get(`${API}/menu`),
        axios.get(`${API}/staff`).catch(() => ({ data: [] }))
      ]);
      
      const activeSessions = dashboardRes.data.active_sessions || [];
      setSessions(activeSessions);
      setCategories(menuRes.data.categories || []);
      setProducts(menuRes.data.products || []);
      
      // Filter only waiters
      const waitersList = (staffRes.data || []).filter(s => s.role === 'waiter');
      setWaiters(waitersList);
      
      const totalPending = activeSessions.reduce((sum, s) => sum + (s.total || 0), 0);
      setStats({
        totalPending,
        sessionCount: activeSessions.length
      });
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);
  useLiveRefresh(fetchSessions, 30000);

  const fetchSessionDetails = async (session) => {
    try {
      const response = await axios.get(`${API}/sessions/${session.session_id}`);
      setSessionDetails(response.data);
      setSelectedSession(session);
      setCheckoutTip(0);
      setTipPercentage(0);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const handleTipPercentage = (percentage) => {
    setTipPercentage(percentage);
    setCheckoutTip((sessionDetails?.total || 0) * percentage / 100);
  };

  const handleCheckout = async () => {
    if (!selectedSession) return;
    
    setProcessing(true);
    try {
      // Calculate actual discount amount
      const sessionTotal = sessionDetails?.total || 0;
      let discountAmount = 0;
      if (discountType === 'percentage') {
        discountAmount = sessionTotal * (manualDiscount / 100);
      } else {
        discountAmount = manualDiscount;
      }
      
      await axios.post(`${API}/sessions/${selectedSession.session_id}/close`, { 
        tip: checkoutTip,
        payment_method: paymentMethod,
        manual_discount: discountAmount,
        discount_reason: discountReason
      }, idem());
      
      toast.success('¡Cuenta cerrada exitosamente!');
      setShowCheckoutDialog(false);
      setSelectedSession(null);
      setSessionDetails(null);
      setManualDiscount(0);
      setDiscountReason('');
      setCheckoutTip(0);
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar cuenta');
    } finally {
      setProcessing(false);
    }
  };

  const calculateDiscountAmount = () => {
    const sessionTotal = sessionDetails?.total || 0;
    if (discountType === 'percentage') {
      return Math.min(sessionTotal * (manualDiscount / 100), sessionTotal);
    }
    return Math.min(manualDiscount, sessionTotal);
  };

  // Split bill functions
  const openSplitDialog = () => {
    setSplitType('equal');
    setSplitParts(2);
    setSplitPercentages([
      { percentage: 50, person_name: 'Parte 1' },
      { percentage: 50, person_name: 'Parte 2' }
    ]);
    setShowSplitDialog(true);
  };

  const handleCreateSplit = async () => {
    if (!selectedSession) return;
    
    setCreatingSplit(true);
    try {
      const payload = {
        split_type: splitType,
        parts: splitParts
      };
      
      if (splitType === 'by_percentage') {
        payload.percentage_splits = splitPercentages;
      }
      
      const response = await axios.post(
        `${API}/sessions/${selectedSession.session_id}/split-bill`,
        payload
      );
      
      toast.success('Cuenta dividida exitosamente');
      setShowSplitDialog(false);
      setSessionSplits(response.data);
      setShowSplitsView(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al dividir cuenta';
      toast.error(errorMsg);
    } finally {
      setCreatingSplit(false);
    }
  };

  const fetchSessionSplits = async () => {
    if (!selectedSession) return;
    
    try {
      const response = await axios.get(`${API}/sessions/${selectedSession.session_id}/splits`);
      setSessionSplits(response.data);
      setShowSplitsView(true);
    } catch (error) {
      toast.error('Error al cargar divisiones');
    }
  };

  const handlePaySplit = async (splitId, amount) => {
    if (!selectedSession) return;
    
    try {
      await axios.post(
        `${API}/sessions/${selectedSession.session_id}/splits/${splitId}/pay`,
        {
          split_id: splitId,
          amount: amount,
          payment_method: paymentMethod,
          tip: 0
        },
        idem()
      );
      
      toast.success('Pago registrado');
      fetchSessionSplits();
      fetchSessions();
    } catch (error) {
      toast.error('Error al registrar pago');
    }
  };

  const copyPaymentLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copiado');
    } catch (error) {
      toast.error('Error al copiar');
    }
  };

  const addPercentagePart = () => {
    setSplitPercentages([
      ...splitPercentages,
      { percentage: 0, person_name: `Parte ${splitPercentages.length + 1}` }
    ]);
  };

  const updatePercentagePart = (index, field, value) => {
    const updated = [...splitPercentages];
    updated[index][field] = field === 'percentage' ? parseFloat(value) || 0 : value;
    setSplitPercentages(updated);
  };

  const removePercentagePart = (index) => {
    if (splitPercentages.length > 2) {
      setSplitPercentages(splitPercentages.filter((_, i) => i !== index));
    }
  };

  const getTotalPercentage = () => {
    return splitPercentages.reduce((sum, p) => sum + (p.percentage || 0), 0);
  };

  // ============== AFIP Invoice Functions ==============
  
  // Check AFIP status on mount
  useEffect(() => {
    const checkAfipStatus = async () => {
      try {
        const response = await axios.get(`${API}/afip/status`);
        setAfipStatus(response.data);
      } catch (error) {
        console.log('AFIP status check failed, setting as configured for UI');
        // Aún así mostrar el botón para que el usuario vea el diálogo
        setAfipStatus({ configured: true, punto_vta: 5, razon_social: 'Pendiente configuración' });
      }
    };
    checkAfipStatus();
  }, []);

  // Fetch closed sessions for invoicing
  const fetchClosedSessions = async () => {
    try {
      const response = await axios.get(`${API}/afip/closed-sessions`);
      setClosedSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Error fetching closed sessions:', error);
    }
  };

  // Open closed sessions dialog
  const openClosedSessionsDialog = async () => {
    await fetchClosedSessions();
    setShowClosedSessionsDialog(true);
  };

  // Invoice a closed session
  const handleInvoiceClosedSession = async () => {
    if (!selectedClosedSession) return;
    
    setInvoicingClosedSession(true);
    try {
      const response = await axios.post(`${API}/afip/invoice-from-session`, {
        session_id: selectedClosedSession.session_id,
        doc_tipo: invoiceDocTipo,
        doc_nro: invoiceDocNro || 0,
        condicion_iva: invoiceCondicionIva
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setInvoiceResult(response.data.invoice);
        setShowInvoiceResult(true);
        setShowClosedSessionsDialog(false);
        setSelectedClosedSession(null);
        // Refresh closed sessions list
        fetchClosedSessions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al emitir factura');
    } finally {
      setInvoicingClosedSession(false);
    }
  };

  // WebSocket listener for notifications
  useEffect(() => {
    if (!lastMessage) return;
    
    const { type, session_id, comensal_id } = lastMessage;
    
    if (type === 'comensal_pagado') {
      // Find comensal name from selected session
      const session = sessions.find(s => s.session_id === session_id);
      if (session) {
        const comensal = session.comensales?.find(c => c.comensal_id === comensal_id);
        toast.success(
          `💰 ${comensal?.nombre || 'Un comensal'} pagó su parte en ${session.unit_name}`,
          { duration: 5000 }
        );
        // Refresh sessions
        fetchSessions();
      }
    }
    
    if (type === 'comensal_added') {
      const session = sessions.find(s => s.session_id === session_id);
      if (session) {
        toast.info(
          `👤 Nueva persona en ${session.unit_name}`,
          { duration: 3000 }
        );
      }
    }
    
    if (type === 'session_closed') {
      fetchSessions();
    }
  }, [lastMessage, sessions]);

  const openInvoiceDialog = () => {
    setInvoiceDocTipo('cf');
    setInvoiceDocNro('');
    setInvoiceCondicionIva('consumidor_final');
    setInvoiceRazonSocial('');
    setInvoiceResult(null);
    setInvoiceItemsMode('detailed');
    
    // Load items from session orders
    const items = [];
    for (const order of (sessionDetails?.orders || [])) {
      for (const item of (order.items || [])) {
        items.push({
          id: `${order.order_id}_${item.product_id || Math.random()}`,
          description: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price || (item.subtotal / item.quantity),
          subtotal: item.subtotal
        });
      }
    }
    setInvoiceItems(items.length > 0 ? items : [{ 
      id: 'default_1', 
      description: 'Consumo gastronómico', 
      quantity: 1, 
      unit_price: sessionDetails?.total || 0,
      subtotal: sessionDetails?.total || 0
    }]);
    
    setShowInvoiceDialog(true);
  };

  const loadDetailedItems = () => {
    const items = [];
    for (const order of (sessionDetails?.orders || [])) {
      for (const item of (order.items || [])) {
        items.push({
          id: `${order.order_id}_${item.product_id || Math.random()}`,
          description: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price || (item.subtotal / item.quantity),
          subtotal: item.subtotal
        });
      }
    }
    setInvoiceItems(items);
    setInvoiceItemsMode('detailed');
  };

  const loadSimpleItem = () => {
    setInvoiceItems([{
      id: 'simple_1',
      description: 'Consumo gastronómico',
      quantity: 1,
      unit_price: sessionDetails?.total || 0,
      subtotal: sessionDetails?.total || 0
    }]);
    setInvoiceItemsMode('simple');
  };

  const addInvoiceItem = () => {
    setInvoiceItems([...invoiceItems, {
      id: `custom_${Date.now()}`,
      description: '',
      quantity: 1,
      unit_price: 0,
      subtotal: 0
    }]);
    setInvoiceItemsMode('custom');
  };

  const updateInvoiceItem = (index, field, value) => {
    const updated = [...invoiceItems];
    if (field === 'quantity' || field === 'unit_price') {
      const numValue = parseFloat(value) || 0;
      updated[index][field] = numValue;
      updated[index].subtotal = updated[index].quantity * updated[index].unit_price;
    } else {
      updated[index][field] = value;
    }
    setInvoiceItems(updated);
    setInvoiceItemsMode('custom');
  };

  const removeInvoiceItem = (index) => {
    if (invoiceItems.length > 1) {
      setInvoiceItems(invoiceItems.filter((_, i) => i !== index));
      setInvoiceItemsMode('custom');
    }
  };

  const getInvoiceItemsTotal = () => {
    return invoiceItems.reduce((sum, item) => sum + (item.subtotal || 0), 0);
  };

  const searchTaxpayer = async () => {
    if (!invoiceDocNro) return;
    
    setSearchingTaxpayer(true);
    try {
      let endpoint = '';
      if (invoiceDocTipo === 'cuit') {
        endpoint = `${API}/afip/taxpayer/${invoiceDocNro}`;
      } else if (invoiceDocTipo === 'dni') {
        endpoint = `${API}/afip/cuit-by-dni/${invoiceDocNro}`;
      }
      
      if (!endpoint) {
        setSearchingTaxpayer(false);
        return;
      }
      
      const response = await axios.get(endpoint);
      
      if (invoiceDocTipo === 'dni' && response.data.found) {
        // Got CUIT from DNI, now get full data
        const cuit = response.data.cuit;
        setInvoiceDocNro(cuit);
        setInvoiceDocTipo('cuit');
        const taxpayerRes = await axios.get(`${API}/afip/taxpayer/${cuit}`);
        setInvoiceRazonSocial(taxpayerRes.data.razon_social || '');
        setInvoiceCondicionIva(taxpayerRes.data.condicion_iva?.toLowerCase().replace(' ', '_') || 'consumidor_final');
        toast.success(`CUIT encontrado: ${cuit}`);
      } else if (invoiceDocTipo === 'cuit') {
        setInvoiceRazonSocial(response.data.razon_social || '');
        setInvoiceCondicionIva(response.data.condicion_iva?.toLowerCase().replace(' ', '_') || 'consumidor_final');
        toast.success('Datos del contribuyente cargados');
      } else {
        toast.error('No se encontró el contribuyente');
      }
    } catch (error) {
      console.error('Error searching taxpayer:', error);
      toast.error('Error al buscar contribuyente');
    } finally {
      setSearchingTaxpayer(false);
    }
  };

  const createInvoice = async () => {
    if (!sessionDetails) return;
    
    // Validate items total matches session total (with small tolerance for rounding)
    const itemsTotal = getInvoiceItemsTotal();
    const sessionTotal = sessionDetails.total || 0;
    
    if (Math.abs(itemsTotal - sessionTotal) > 1) {
      toast.error(`El total de items ($${itemsTotal.toLocaleString()}) no coincide con el total de la cuenta ($${sessionTotal.toLocaleString()})`);
      return;
    }
    
    setCreatingInvoice(true);
    try {
      const items = invoiceItems.map(item => ({
        product_name: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        subtotal: item.subtotal
      }));
      
      const response = await axios.post(`${API}/afip/invoice`, {
        session_id: selectedSession.session_id,
        importe_total: sessionTotal,
        concepto: 2, // Servicios
        doc_tipo: invoiceDocTipo,
        doc_nro: invoiceDocTipo === 'cf' ? 0 : parseInt(invoiceDocNro) || 0,
        condicion_iva: invoiceCondicionIva,
        items: items
      });
      
      if (response.data.success) {
        setInvoiceResult({
          ...response.data.invoice,
          items: items // Include items in result for printing
        });
        setShowInvoiceDialog(false);
        setShowInvoiceResult(true);
        toast.success(`Factura C N° ${response.data.invoice.cbte_nro} emitida con éxito`);
        
        // Refresh session to show invoice reference
        fetchSessions();
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(error.response?.data?.detail || 'Error al emitir factura');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const printInvoice = () => {
    if (!invoiceResult) return;
    
    // Build items HTML
    const itemsHtml = (invoiceResult.items || []).map(item => `
      <div class="item">
        <span>${item.quantity} x ${item.product_name}</span>
        <span>$ ${item.subtotal?.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
      </div>
    `).join('');
    
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Factura C</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          body { font-family: monospace; font-size: 12px; padding: 10px; max-width: 80mm; margin: 0 auto; }
          .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
          .header h1 { font-size: 14px; margin: 0; }
          .header p { margin: 2px 0; font-size: 10px; }
          .tipo-cbte { font-size: 24px; font-weight: bold; border: 2px solid #000; padding: 5px 15px; display: inline-block; margin: 10px 0; }
          .info { margin: 10px 0; }
          .info div { display: flex; justify-content: space-between; margin: 3px 0; }
          .items { margin: 10px 0; border-top: 1px dashed #000; padding-top: 10px; }
          .item { display: flex; justify-content: space-between; margin: 4px 0; font-size: 11px; }
          .total { font-size: 16px; font-weight: bold; border-top: 1px dashed #000; padding-top: 10px; margin-top: 10px; display: flex; justify-content: space-between; }
          .cae { margin-top: 15px; padding-top: 10px; border-top: 1px dashed #000; font-size: 10px; }
          .qr { text-align: center; margin-top: 15px; }
          .qr img { max-width: 150px; }
          .footer { text-align: center; margin-top: 15px; font-size: 10px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${afipStatus?.razon_social || 'QR Mesa'}</h1>
          <p>CUIT: ${afipStatus?.cuit || ''}</p>
          <p>${afipStatus?.domicilio || ''}</p>
          <div class="tipo-cbte">C</div>
          <p>FACTURA</p>
        </div>
        
        <div class="info">
          <div><span>Punto de Venta:</span><span>${String(invoiceResult.punto_vta).padStart(4, '0')}</span></div>
          <div><span>Comp. Nro:</span><span>${String(invoiceResult.cbte_nro).padStart(8, '0')}</span></div>
          <div><span>Fecha:</span><span>${invoiceResult.fecha?.replace(/(\d{4})(\d{2})(\d{2})/, '$3/$2/$1')}</span></div>
        </div>
        
        <div class="info">
          <div><span>Consumidor Final</span></div>
        </div>
        
        ${itemsHtml ? `<div class="items">${itemsHtml}</div>` : ''}
        
        <div class="total">
          <span>TOTAL</span>
          <span>$ ${invoiceResult.importe_total?.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</span>
        </div>
        
        <div class="cae">
          <div><strong>CAE:</strong> ${invoiceResult.cae}</div>
          <div><strong>Vto CAE:</strong> ${invoiceResult.cae_vto?.replace(/(\d{4})(\d{2})(\d{2})/, '$3/$2/$1')}</div>
        </div>
        
        <div class="qr">
          <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(invoiceResult.qr_url)}" alt="QR Fiscal" />
        </div>
        
        <div class="footer">
          <p>Comprobante Autorizado por AFIP</p>
        </div>
        
        <script>
          window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 500); };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleSendWhatsApp = () => {
    if (!whatsAppPhone || !sessionDetails) return;
    
    const message = encodeURIComponent(
      `🧾 *Cuenta - ${sessionDetails.unit_name}*\n\n` +
      `Total: $${(sessionDetails.total || 0).toLocaleString()}\n\n` +
      `Gracias por su visita!`
    );
    
    window.open(`https://wa.me/${whatsAppPhone.replace(/\D/g, '')}?text=${message}`, '_blank');
    setShowWhatsAppDialog(false);
    setWhatsAppPhone('');
  };

  // Print ticket function - now shows preview first based on settings
  const handlePrintTicket = async () => {
    if (!selectedSession) return;
    
    setPrintingTicket(true);
    try {
      // Get settings to check if preview is enabled
      let showPreview = true;
      try {
        const settingsRes = await axios.get(`${API}/business/settings`);
        showPreview = settingsRes.data?.show_print_preview !== false;
      } catch (e) {
        console.log('Could not get settings, using default preview');
      }
      
      // First, get the cashier printer (if configured)
      let printerId = null;
      try {
        const printersRes = await axios.get(`${API}/printers`);
        const cashierPrinter = (printersRes.data || []).find(p => 
          p.areas.includes('cashier') && p.is_active
        );
        if (cashierPrinter) {
          printerId = cashierPrinter.printer_id;
        }
      } catch (e) {
        console.log('No printers configured');
      }
      
      const response = await axios.post(`${API}/print/ticket/${selectedSession.session_id}`, {
        include_tips_suggestion: true,
        printer_id: printerId
      });
      
      const { type, html, ip, port, commands } = response.data;
      
      if (type === 'thermal_network' && ip && commands) {
        // Send directly to thermal printer via backend TCP socket
        try {
          const printResult = await axios.post(`${API}/print/send-to-printer`, { 
            ip, 
            port: port || 9100, 
            commands 
          });
          if (printResult.data.success) {
            toast.success('Ticket enviado a impresora');
          } else {
            throw new Error(printResult.data.error || 'Error de impresora');
          }
        } catch (printError) {
          console.error('Thermal print error:', printError);
          // Fallback to preview or direct print
          if (html) {
            if (showPreview) {
              setTicketHtml(html);
              setShowTicketPreview(true);
            } else {
              printDirect(html);
            }
          }
        }
      } else if (html) {
        // Show preview modal or print directly based on settings
        if (showPreview) {
          setTicketHtml(html);
          setShowTicketPreview(true);
        } else {
          printDirect(html);
        }
      } else {
        toast.info('No se pudo generar el ticket');
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Error al generar ticket');
    } finally {
      setPrintingTicket(false);
    }
  };

  // Print directly without preview
  const printDirect = (html) => {
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // Actually print after preview
  const handleConfirmPrint = () => {
    if (!ticketHtml) return;
    printDirect(ticketHtml);
    setShowTicketPreview(false);
    setTicketHtml('');
  };

  // Assign waiter to session
  const handleAssignWaiter = async (waiterId) => {
    if (!selectedSession || !waiterId) return;
    
    try {
      await axios.post(`${API}/sessions/${selectedSession.session_id}/assign-waiter`, {
        waiter_id: waiterId
      });
      
      // Refresh session details
      fetchSessionDetails(selectedSession);
      toast.success('Mozo asignado');
    } catch (error) {
      toast.error('Error al asignar mozo');
    }
  };

  // Cart functions for adding items
  const openAddItemsDialog = () => {
    setCart([]);
    setOrderNotes('');
    setSelectedCategory(null);
    setShowAddItemsDialog(true);
  };

  const addToCart = (product) => {
    const existingIndex = cart.findIndex(item => item.product_id === product.product_id);
    if (existingIndex >= 0) {
      const newCart = [...cart];
      newCart[existingIndex].quantity += 1;
      setCart(newCart);
    } else {
      setCart([...cart, {
        product_id: product.product_id,
        product: product,
        quantity: 1,
        notes: ''
      }]);
    }
  };

  const updateCartQuantity = (index, delta) => {
    const newCart = [...cart];
    newCart[index].quantity += delta;
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    }
    setCart(newCart);
  };

  const submitOrder = async () => {
    if (cart.length === 0 || !selectedSession) {
      toast.error('Agrega items al pedido');
      return;
    }
    
    setSubmittingOrder(true);
    try {
      await axios.post(`${API}/sessions/${selectedSession.session_id}/orders`, {
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          notes: item.notes
        })),
        notes: orderNotes
      }, idem());
      
      toast.success('¡Pedido agregado!');
      setShowAddItemsDialog(false);
      setCart([]);
      setOrderNotes('');
      // Refresh session details
      fetchSessionDetails(selectedSession);
      fetchSessions();
    } catch (error) {
      toast.error('Error al agregar pedido');
    } finally {
      setSubmittingOrder(false);
    }
  };

  const filteredProducts = selectedCategory 
    ? products.filter(p => p.category_id === selectedCategory)
    : products;
  const cartTotal = cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);

  const removeOrderItem = async (orderId, itemIndex) => {
    try {
      await axios.delete(`${API}/orders/${orderId}/items/${itemIndex}`);
      toast.success('Ítem eliminado');
      // Refresh session details
      fetchSessionDetails(selectedSession);
      fetchSessions();
    } catch (error) {
      toast.error('Error al eliminar ítem');
    }
  };

  const openMoveItemDialog = (orderId, itemIndex, item) => {
    setItemToMove({ orderId, itemIndex, item });
    setShowMoveItemDialog(true);
  };

  const moveItemToParticipant = async (participant) => {
    if (!itemToMove) return;
    
    try {
      await axios.post(`${API}/orders/${itemToMove.orderId}/items/${itemToMove.itemIndex}/move`, {
        target_participant_id: participant.id,
        target_participant_name: participant.name
      });
      toast.success(`Ítem movido a ${participant.name}`);
      setShowMoveItemDialog(false);
      setItemToMove(null);
      fetchSessionDetails(selectedSession);
      fetchSessions();
    } catch (error) {
      toast.error('Error al mover ítem');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getOrderStatusColor = (status) => {
    const colors = {
      'Nuevo': 'bg-blue-500/20 text-blue-800 border-blue-500/30',
      'Confirmado': 'bg-purple-500/20 text-purple-700 border-purple-500/30',
      'En preparación': 'bg-orange-500/20 text-orange-800 border-orange-500/30',
      'Listo': 'bg-green-500/20 text-green-800 border-green-500/30',
      'Entregado': 'bg-slate-500/20 text-slate-600 border-slate-500/30'
    };
    return colors[status] || colors['Nuevo'];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-emerald-600 dark:text-emerald-300">Cargando caja...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-xl border-b border-emerald-500/20">
        <div className="px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Back to Dashboard */}
            {isAdmin(user) && (
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-foreground/70 hover:text-foreground hover:bg-accent/10"
              title="Volver al Panel"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            )}
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <CreditCard className="w-7 h-7 text-foreground" />
            </div>
            <div>
              <h1 className="font-bold text-xl text-foreground">CAJA</h1>
              <p className="text-sm text-emerald-700">{user?.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={fetchSessions} className="text-foreground/70 hover:text-foreground hover:bg-accent/10">
              <RefreshCw className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/perfil')} className="text-foreground/70 hover:text-foreground hover:bg-accent/10" data-testid="profile-link" title="Mi perfil y apariencia">
              <UserCog className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-foreground/70 hover:text-foreground hover:bg-accent/10">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-muted/50 px-4 md:px-6 py-4 border-b border-border">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-emerald-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-foreground">${stats.totalPending.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">Total Pendiente</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/30 flex items-center justify-center">
                <Receipt className="w-7 h-7 text-violet-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-foreground">{stats.sessionCount}</p>
                <p className="text-sm text-muted-foreground">Cuentas Activas</p>
              </div>
            </div>
          </div>
          
          {/* Invoice Closed Session Button */}
          {afipStatus?.configured && (
            <Button 
              onClick={openClosedSessionsDialog}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
              data-testid="invoice-closed-session-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Facturar Mesa Cerrada
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row min-h-[calc(100vh-180px)]">
        {/* Sessions List */}
        <div className="lg:w-96 border-r border-border bg-muted/30">
          <div className="p-4 border-b border-border">
            <h2 className="font-bold text-lg text-foreground">Cuentas por Cobrar</h2>
            <p className="text-sm text-muted-foreground">Selecciona para ver detalle</p>
          </div>
          <ScrollArea className="h-[calc(100vh-280px)]">
            <div className="p-4 space-y-3">
              {sessions.length === 0 ? (
                <div className="text-center py-12">
                  <Wallet className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No hay cuentas activas</p>
                </div>
              ) : (
                sessions.map((session) => (
                  <Card 
                    key={session.session_id}
                    className={`bg-muted/50 border-border cursor-pointer transition-all hover:bg-border/50 hover:scale-[1.02] ${
                      selectedSession?.session_id === session.session_id ? 'ring-2 ring-emerald-500 shadow-lg shadow-emerald-500/20' : ''
                    }`}
                    onClick={() => fetchSessionDetails(session)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-lg text-foreground">{session.unit_name || 'Mesa'}</span>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className="gap-1" data-testid={`cashier-session-devices-${session.session_id}`} title="Celulares con la mesa abierta">
                            <Smartphone className="w-3 h-3" />{session.devices || 0}
                          </Badge>
                          <Badge className="bg-emerald-500/20 text-emerald-800 border-emerald-500/30">
                            {session.orders_count || 0} pedidos
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm">
                            {session.started_at ? new Date(session.started_at).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                          </span>
                        </div>
                        <span className="text-2xl font-bold text-emerald-700">
                          ${(session.total || 0).toLocaleString()}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Session Details */}
        <div className="flex-1 p-6">
          {selectedSession && sessionDetails ? (
            <div className="max-w-2xl mx-auto">
              <Card className="bg-muted/50 border-border overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-emerald-500 to-teal-500" />
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-2xl text-foreground">{sessionDetails.unit_name || 'Mesa'}</CardTitle>
                      <CardDescription className="text-muted-foreground">
                        Iniciada: {sessionDetails.started_at ? new Date(sessionDetails.started_at).toLocaleString('es') : '--'}
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-emerald-500 text-foreground text-lg px-4 py-2">
                        ${(sessionDetails.total || 0).toLocaleString()}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Participants (for individual mode) */}
                  {sessionDetails.participants?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-bold mb-4 text-foreground flex items-center gap-2">
                        <Users className="w-5 h-5 text-violet-500" />
                        Participantes ({sessionDetails.participants.length})
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {sessionDetails.participants.map((participant, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className="px-3 py-1.5 border-violet-300 bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300"
                          >
                            <User className="w-3 h-3 mr-1.5" />
                            {participant.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Orders */}
                  <div className="mb-6">
                    <h3 className="font-bold mb-4 text-foreground flex items-center gap-2">
                      <Receipt className="w-5 h-5 text-emerald-400" />
                      Detalle de consumo
                    </h3>
                    <ScrollArea className="h-[280px] pr-2">
                      <div className="space-y-4">
                        {sessionDetails.orders?.map((order, idx) => (
                          <div key={idx} className="bg-muted rounded-xl p-4">
                            <div className="flex justify-between items-center mb-3">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">
                                  Pedido #{order.order_id?.slice(-6)}
                                </span>
                                {order.participant_name && (
                                  <Badge variant="outline" className="text-xs border-violet-300 text-violet-600 dark:text-violet-400">
                                    <User className="w-3 h-3 mr-1" />
                                    {order.participant_name}
                                  </Badge>
                                )}
                              </div>
                              <Badge className={getOrderStatusColor(order.status)}>
                                {order.status}
                              </Badge>
                            </div>
                            <div className="space-y-2">
                              {order.items?.map((item, iIdx) => (
                                <div key={iIdx} className="flex justify-between items-center text-sm group">
                                  <span className="text-muted-foreground">{item.quantity}x {item.product_name || item.name}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-emerald-700 font-mono">
                                      ${(item.subtotal || (item.unit_price || item.price || 0) * item.quantity).toLocaleString()}
                                    </span>
                                    {order.status !== 'Entregado' && order.status !== 'Cancelado' && (
                                      <>
                                        {/* Move item button - only show if there are other participants */}
                                        {sessionDetails.participants?.length > 1 && (
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 text-blue-400 hover:text-blue-500 hover:bg-blue-500/10"
                                            onClick={() => openMoveItemDialog(order.order_id, iIdx, item)}
                                            title="Mover a otra cuenta"
                                          >
                                            <ArrowRightLeft className="w-3 h-3" />
                                          </Button>
                                        )}
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6 text-red-400 hover:text-red-500 hover:bg-red-500/10"
                                          onClick={() => removeOrderItem(order.order_id, iIdx)}
                                          title="Eliminar ítem"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>

                  {/* Totals */}
                  <div className="border-t border-border pt-4 space-y-2">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Subtotal</span>
                      <span className="font-mono">${(sessionDetails.subtotal || sessionDetails.total || 0).toLocaleString()}</span>
                    </div>
                    {sessionDetails.service_charge > 0 && (
                      <div className="flex justify-between text-muted-foreground text-sm">
                        <span>Servicio</span>
                        <span className="font-mono">${sessionDetails.service_charge.toLocaleString()}</span>
                      </div>
                    )}
                    {sessionDetails.tax > 0 && (
                      <div className="flex justify-between text-muted-foreground text-sm">
                        <span>Impuestos</span>
                        <span className="font-mono">${sessionDetails.tax.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-2xl font-bold pt-2 border-t border-border">
                      <span className="text-foreground">Total</span>
                      <span className="text-emerald-700">${(sessionDetails.total || 0).toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-6 grid grid-cols-4 gap-2">
                    <Button 
                      variant="outline"
                      className="border-violet-500/50 text-violet-600 dark:text-violet-400 hover:bg-violet-500/20"
                      onClick={openAddItemsDialog}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Agregar</span>
                    </Button>
                    <Button 
                      variant="outline"
                      className="border-blue-500/50 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20"
                      onClick={handlePrintTicket}
                      disabled={printingTicket}
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">{printingTicket ? '...' : 'Ticket'}</span>
                    </Button>
                    <Button 
                      variant="outline"
                      className="border-input text-muted-foreground hover:bg-muted"
                      onClick={() => setShowWhatsAppDialog(true)}
                    >
                      <MessageSquare className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">WhatsApp</span>
                    </Button>
                    {/* Split Bill Buttons */}
                    <div className="flex flex-wrap gap-1">
                      {sessionDetails?.is_split ? (
                        <Button 
                          variant="outline"
                          className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                          onClick={fetchSessionSplits}
                          title="Ver división actual por partes"
                        >
                          <Percent className="w-4 h-4 mr-1" />
                          <span className="hidden sm:inline">Ver División</span>
                        </Button>
                      ) : (
                        <Button 
                          variant="outline"
                          className="border-input text-muted-foreground hover:bg-muted"
                          onClick={openSplitDialog}
                          title="División rápida por partes o porcentaje"
                        >
                          <Percent className="w-4 h-4 mr-1" />
                          <span className="hidden sm:inline">Dividir</span>
                        </Button>
                      )}
                      <Button 
                        variant="outline"
                        className="border-violet-500 text-violet-500 hover:bg-violet-50 dark:hover:bg-violet-950"
                        onClick={() => setShowComensalesSplit(true)}
                        title="División por comensales con items asignados"
                        data-testid="split-comensales-btn"
                      >
                        <Users className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Comensales</span>
                      </Button>
                    </div>
                    {/* AFIP Invoice Button */}
                    {afipStatus?.configured && (
                      <Button 
                        variant="outline"
                        className="border-blue-500 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950"
                        onClick={openInvoiceDialog}
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">Facturar</span>
                      </Button>
                    )}
                    <Button 
                      className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                      onClick={() => setShowCheckoutDialog(true)}
                    >
                      <CreditCard className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Cobrar</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mb-6">
                <Receipt className="w-12 h-12 text-muted-foreground" />
              </div>
              <h2 className="text-xl font-bold text-muted-foreground mb-2">Selecciona una cuenta</h2>
              <p className="text-muted-foreground">Elige una cuenta de la lista para ver el detalle</p>
            </div>
          )}
        </div>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent className="bg-card border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-400" />
              Cerrar Cuenta
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {sessionDetails?.unit_name} - Total: ${(sessionDetails?.total || 0).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Split by participant option (for individual mode) */}
            {sessionDetails?.participants?.length > 0 && (
              <div className="p-4 bg-violet-50 dark:bg-violet-950/30 rounded-xl border border-violet-200 dark:border-violet-800">
                <h4 className="font-semibold text-violet-700 dark:text-violet-300 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Dividir por Participante
                </h4>
                <div className="space-y-2">
                  {sessionDetails.participants.map((participant, idx) => {
                    // Calculate participant's subtotal from orders
                    const participantTotal = sessionDetails.orders
                      ?.filter(o => o.participant_id === participant.participant_id)
                      .reduce((sum, o) => sum + (o.subtotal || 0), 0) || 0;
                    return (
                      <div key={idx} className="flex justify-between items-center p-2 bg-background rounded-lg">
                        <span className="flex items-center gap-2 text-foreground">
                          <User className="w-4 h-4 text-violet-500" />
                          {participant.name}
                        </span>
                        <span className="font-mono font-semibold text-violet-600 dark:text-violet-400">
                          ${participantTotal.toLocaleString()}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-violet-600 dark:text-violet-400 mt-2">
                  💡 Cada participante puede pagar su parte individualmente
                </p>
              </div>
            )}

            {/* Discount / Courtesy */}
            <div className="p-4 bg-orange-50 dark:bg-orange-950/30 rounded-xl border border-orange-200 dark:border-orange-800">
              <h4 className="font-semibold text-orange-700 dark:text-orange-300 mb-3 flex items-center gap-2">
                <Gift className="w-4 h-4" />
                Descuento / Cortesía
              </h4>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className={discountType === 'fixed' ? 'bg-orange-100 border-orange-400' : ''}
                    onClick={() => setDiscountType('fixed')}
                  >
                    <DollarSign className="w-4 h-4 mr-1" />
                    Monto fijo
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={discountType === 'percentage' ? 'bg-orange-100 border-orange-400' : ''}
                    onClick={() => setDiscountType('percentage')}
                  >
                    <Percent className="w-4 h-4 mr-1" />
                    Porcentaje
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min="0"
                    value={manualDiscount || ''}
                    onChange={(e) => setManualDiscount(parseFloat(e.target.value) || 0)}
                    className="flex-1 bg-background"
                    placeholder={discountType === 'percentage' ? '% descuento' : '$ descuento'}
                  />
                  {discountType === 'percentage' && (
                    <div className="flex items-center gap-1">
                      {[10, 20, 50, 100].map((pct) => (
                        <Button
                          key={pct}
                          variant="outline"
                          size="sm"
                          className="px-2"
                          onClick={() => setManualDiscount(pct)}
                        >
                          {pct}%
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
                <Input
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  className="bg-background"
                  placeholder="Motivo del descuento (ej: cortesía del chef)"
                />
                {manualDiscount > 0 && (
                  <p className="text-sm text-orange-600 dark:text-orange-400">
                    Descuento aplicado: -${calculateDiscountAmount().toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            {/* Waiter Assignment for Tips */}
            {waiters.length > 0 && (
              <div className="p-4 bg-sky-50 dark:bg-sky-950/30 rounded-xl border border-sky-200 dark:border-sky-800">
                <h4 className="font-semibold text-sky-700 dark:text-sky-300 mb-3 flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />
                  Mozo (para propina)
                </h4>
                <Select 
                  value={sessionDetails?.waiter_id || selectedWaiter} 
                  onValueChange={(value) => {
                    setSelectedWaiter(value);
                    handleAssignWaiter(value);
                  }}
                >
                  <SelectTrigger className="bg-background border-sky-300 dark:border-sky-700">
                    <SelectValue placeholder="Seleccionar mozo">
                      {sessionDetails?.waiter_name || 'Sin asignar'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {waiters.map((w) => (
                      <SelectItem key={w.user_id} value={w.user_id}>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          {w.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {sessionDetails?.waiter_name && (
                  <p className="text-xs text-sky-600 dark:text-sky-400 mt-2">
                    ✓ Mozo asignado: {sessionDetails.waiter_name}
                  </p>
                )}
              </div>
            )}

            {/* Tip */}
            <div>
              <Label className="text-muted-foreground mb-3 block">Propina sugerida</Label>
              <div className="grid grid-cols-4 gap-2">
                {[0, 10, 15, 20].map((pct) => (
                  <Button
                    key={pct}
                    variant="outline"
                    className={`${
                      tipPercentage === pct
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-600 dark:text-emerald-400'
                        : 'border-input text-muted-foreground hover:bg-muted'
                    }`}
                    onClick={() => handleTipPercentage(pct)}
                  >
                    {pct}%
                  </Button>
                ))}
              </div>
              <Input
                type="number"
                value={checkoutTip}
                onChange={(e) => {
                  setCheckoutTip(parseFloat(e.target.value) || 0);
                  setTipPercentage(0);
                }}
                className="mt-3 bg-muted border-input text-foreground"
                placeholder="Propina personalizada"
              />
            </div>

            {/* Payment Method */}
            <div>
              <Label className="text-muted-foreground mb-3 block">Método de pago</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="bg-muted border-input text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-muted border-input">
                  <SelectItem value="cash">
                    <div className="flex items-center gap-2">
                      <Banknote className="w-4 h-4" />
                      Efectivo
                    </div>
                  </SelectItem>
                  <SelectItem value="card">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Tarjeta
                    </div>
                  </SelectItem>
                  <SelectItem value="transfer">
                    <div className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Transferencia
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Summary */}
            <div className="bg-muted rounded-xl p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="text-foreground">${(sessionDetails?.total || 0).toLocaleString()}</span>
              </div>
              {manualDiscount > 0 && (
                <div className="flex justify-between text-orange-500">
                  <span>Descuento {discountReason && `(${discountReason})`}</span>
                  <span>-${calculateDiscountAmount().toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Propina</span>
                <span className="text-emerald-700">+${checkoutTip.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xl font-bold pt-2 border-t border-border">
                <span className="text-foreground">Total a cobrar</span>
                <span className="text-emerald-700">
                  ${((sessionDetails?.total || 0) - calculateDiscountAmount() + checkoutTip).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowCheckoutDialog(false)} className="text-muted-foreground">
              Cancelar
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleCheckout}
              disabled={processing}
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Confirmar Cobro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Dialog */}
      <Dialog open={showWhatsAppDialog} onOpenChange={setShowWhatsAppDialog}>
        <DialogContent className="bg-card border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-400" />
              Enviar por WhatsApp
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Envía el detalle de la cuenta al cliente
            </DialogDescription>
          </DialogHeader>

          <div>
            <Label className="text-muted-foreground mb-2 block">Número de WhatsApp</Label>
            <Input
              type="tel"
              placeholder="+54 9 11 1234-5678"
              value={whatsAppPhone}
              onChange={(e) => setWhatsAppPhone(e.target.value)}
              className="bg-muted border-input text-foreground"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowWhatsAppDialog(false)} className="text-muted-foreground">
              Cancelar
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleSendWhatsApp}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Items Dialog */}
      <Dialog open={showAddItemsDialog} onOpenChange={setShowAddItemsDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] bg-card border-border text-foreground p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Plus className="w-6 h-6" />
              </div>
              Agregar pedido - {sessionDetails?.unit_name}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Selecciona los productos para agregar al pedido
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col md:flex-row h-[60vh]">
            {/* Products section */}
            <div className="flex-1 p-6 border-r border-border">
              {/* Category tabs */}
              <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                <Button
                  size="sm"
                  variant={!selectedCategory ? 'default' : 'outline'}
                  className={!selectedCategory ? 'bg-emerald-600' : 'border-input'}
                  onClick={() => setSelectedCategory(null)}
                >
                  Todo
                </Button>
                {categories.map((cat) => (
                  <Button
                    key={cat.category_id}
                    size="sm"
                    variant={selectedCategory === cat.category_id ? 'default' : 'outline'}
                    className={selectedCategory === cat.category_id ? 'bg-emerald-600' : 'border-input'}
                    onClick={() => setSelectedCategory(cat.category_id)}
                  >
                    {cat.name}
                  </Button>
                ))}
              </div>

              {/* Products grid */}
              <ScrollArea className="h-[calc(60vh-120px)]">
                <div className="grid grid-cols-2 gap-3">
                  {filteredProducts.map((product) => (
                    <Card 
                      key={product.product_id}
                      className="bg-muted border-border cursor-pointer hover:bg-accent transition-colors"
                      onClick={() => addToCart(product)}
                    >
                      <CardContent className="p-3">
                        <p className="font-semibold text-foreground text-sm truncate">{product.name}</p>
                        <p className="text-emerald-600 dark:text-emerald-400 font-bold">${product.price.toLocaleString()}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Cart section */}
            <div className="w-full md:w-80 p-6 bg-muted flex flex-col">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                Pedido ({cart.length})
              </h3>

              {cart.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-muted-foreground text-center">
                    Toca un producto para agregarlo
                  </p>
                </div>
              ) : (
                <>
                  <ScrollArea className="flex-1 mb-4">
                    <div className="space-y-3">
                      {cart.map((item, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-background rounded-xl">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-foreground text-sm truncate">{item.product.name}</p>
                            <p className="text-emerald-600 dark:text-emerald-400 text-sm">${(item.product.price * item.quantity).toLocaleString()}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="icon" 
                              variant="outline" 
                              className="w-8 h-8 border-input"
                              onClick={() => updateCartQuantity(index, -1)}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-8 text-center font-bold">{item.quantity}</span>
                            <Button 
                              size="icon" 
                              variant="outline" 
                              className="w-8 h-8 border-input"
                              onClick={() => updateCartQuantity(index, 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  <Textarea
                    placeholder="Notas del pedido (opcional)"
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    className="mb-4 bg-background border-input text-foreground"
                    rows={2}
                  />

                  <div className="flex justify-between items-center mb-4 text-lg font-bold">
                    <span>Total</span>
                    <span className="text-emerald-600 dark:text-emerald-400">${cartTotal.toLocaleString()}</span>
                  </div>

                  <Button 
                    className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                    onClick={submitOrder}
                    disabled={submittingOrder}
                  >
                    {submittingOrder ? (
                      <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Send className="w-5 h-5 mr-2" />
                    )}
                    Enviar Pedido
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move Item Dialog */}
      <Dialog open={showMoveItemDialog} onOpenChange={setShowMoveItemDialog}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-500" />
              Mover ítem a otra cuenta
            </DialogTitle>
            <DialogDescription>
              {itemToMove && (
                <span>Mover <strong>{itemToMove.item.product_name || itemToMove.item.name}</strong> a:</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 py-4">
            {sessionDetails?.participants?.map((participant) => (
              <Button
                key={participant.id}
                variant="outline"
                className="w-full justify-start h-auto py-3"
                onClick={() => moveItemToParticipant(participant)}
              >
                <User className="w-4 h-4 mr-3 text-violet-500" />
                <span>{participant.name}</span>
              </Button>
            ))}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMoveItemDialog(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Preview Modal */}
      <Dialog open={showTicketPreview} onOpenChange={setShowTicketPreview}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-500" />
              Vista Previa del Ticket
            </DialogTitle>
            <DialogDescription>
              Revisá el ticket antes de imprimir
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto bg-white rounded-lg border shadow-inner my-4" style={{ maxHeight: '50vh' }}>
            <iframe
              srcDoc={ticketHtml}
              title="Ticket Preview"
              className="w-full h-full min-h-[400px]"
              style={{ border: 'none' }}
            />
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowTicketPreview(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmPrint}
              className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Bill Dialog */}
      <Dialog open={showSplitDialog} onOpenChange={setShowSplitDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-500" />
              Dividir Cuenta
            </DialogTitle>
            <DialogDescription>
              Total: ${(sessionDetails?.total || 0).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Split Type Selection */}
            <div className="space-y-2">
              <Label>Tipo de división</Label>
              <Select value={splitType} onValueChange={setSplitType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equal">Partes iguales</SelectItem>
                  <SelectItem value="by_percentage">Por porcentaje</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Equal Split Options */}
            {splitType === 'equal' && (
              <div className="space-y-2">
                <Label>Cantidad de partes</Label>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setSplitParts(Math.max(2, splitParts - 1))}
                    disabled={splitParts <= 2}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-2xl font-bold w-16 text-center">{splitParts}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setSplitParts(Math.min(10, splitParts + 1))}
                    disabled={splitParts >= 10}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Cada parte: ${Math.round((sessionDetails?.total || 0) / splitParts).toLocaleString()}
                </p>
              </div>
            )}

            {/* Percentage Split Options */}
            {splitType === 'by_percentage' && (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label>Distribución por porcentaje</Label>
                  <Button variant="outline" size="sm" onClick={addPercentagePart}>
                    <Plus className="w-3 h-3 mr-1" />
                    Agregar
                  </Button>
                </div>
                <ScrollArea className="h-48">
                  <div className="space-y-2 pr-2">
                    {splitPercentages.map((part, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                        <Input
                          value={part.person_name}
                          onChange={(e) => updatePercentagePart(index, 'person_name', e.target.value)}
                          className="flex-1"
                          placeholder="Nombre"
                        />
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={part.percentage}
                            onChange={(e) => updatePercentagePart(index, 'percentage', e.target.value)}
                            className="w-20"
                            min="0"
                            max="100"
                          />
                          <span className="text-muted-foreground">%</span>
                        </div>
                        <span className="text-sm font-mono w-24 text-right">
                          ${Math.round((sessionDetails?.total || 0) * (part.percentage / 100)).toLocaleString()}
                        </span>
                        {splitPercentages.length > 2 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500"
                            onClick={() => removePercentagePart(index)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <div className={`text-sm ${getTotalPercentage() === 100 ? 'text-green-600' : 'text-red-500'}`}>
                  Total: {getTotalPercentage()}% {getTotalPercentage() !== 100 && '(debe ser 100%)'}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSplitDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateSplit}
              disabled={creatingSplit || (splitType === 'by_percentage' && getTotalPercentage() !== 100)}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {creatingSplit ? 'Dividiendo...' : 'Dividir Cuenta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Splits View Dialog */}
      <Dialog open={showSplitsView} onOpenChange={setShowSplitsView}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-500" />
              Cuenta Dividida
            </DialogTitle>
            <DialogDescription>
              Total: ${sessionSplits?.total?.toLocaleString()} | {sessionSplits?.all_paid ? 'Todo pagado' : 'Pagos pendientes'}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-96">
            <div className="space-y-3">
              {sessionSplits?.splits?.map((split) => (
                <Card key={split.split_id} className={`${split.status === 'paid' ? 'bg-green-50 dark:bg-green-950/20' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="font-semibold">{split.person_name}</span>
                          {split.status === 'paid' && (
                            <Badge className="bg-green-500">Pagado</Badge>
                          )}
                        </div>
                        {split.percentage && (
                          <span className="text-xs text-muted-foreground">{split.percentage}%</span>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">${split.amount?.toLocaleString()}</p>
                        {split.paid_at && (
                          <p className="text-xs text-muted-foreground">
                            {new Date(split.paid_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {split.status !== 'paid' && (
                      <div className="mt-3 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => copyPaymentLink(split.payment_link)}
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copiar Link
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(split.payment_link, '_blank')}
                        >
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handlePaySplit(split.split_id, split.amount)}
                        >
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Cobrar
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSplitsView(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AFIP Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Emitir Factura C
            </DialogTitle>
            <DialogDescription>
              Total: ${(sessionDetails?.total || 0).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Items Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Detalle</Label>
                <div className="flex gap-1">
                  <Button 
                    variant={invoiceItemsMode === 'detailed' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={loadDetailedItems}
                    className="h-7 text-xs"
                  >
                    Detallado
                  </Button>
                  <Button 
                    variant={invoiceItemsMode === 'simple' ? 'default' : 'outline'} 
                    size="sm"
                    onClick={loadSimpleItem}
                    className="h-7 text-xs"
                  >
                    Simple
                  </Button>
                </div>
              </div>

              {/* Items List */}
              <div className="space-y-1 bg-muted/50 rounded-lg p-2 max-h-48 overflow-y-auto">
                {invoiceItems.map((item, index) => (
                  <div key={item.id} className="flex items-center gap-1 bg-background rounded p-1.5 text-sm">
                    <Input
                      value={item.quantity}
                      onChange={(e) => updateInvoiceItem(index, 'quantity', e.target.value)}
                      className="w-12 h-8 text-center text-sm p-1"
                      type="number"
                      min="1"
                    />
                    <span className="text-muted-foreground text-xs">x</span>
                    <Input
                      value={item.description}
                      onChange={(e) => updateInvoiceItem(index, 'description', e.target.value)}
                      placeholder="Descripción"
                      className="flex-1 h-8 text-sm p-1"
                    />
                    <span className="text-muted-foreground text-xs">$</span>
                    <Input
                      value={item.unit_price}
                      onChange={(e) => updateInvoiceItem(index, 'unit_price', e.target.value)}
                      className="w-20 h-8 text-right text-sm p-1"
                      type="number"
                      step="0.01"
                    />
                    <span className="w-20 text-right font-mono text-xs text-muted-foreground">
                      =${item.subtotal?.toLocaleString()}
                    </span>
                    {invoiceItems.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-red-500"
                        onClick={() => removeInvoiceItem(index)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full h-7 text-xs"
                  onClick={addInvoiceItem}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Agregar
                </Button>
              </div>

              {/* Total comparison */}
              <div className={`flex justify-between items-center p-2 rounded-lg text-sm ${
                Math.abs(getInvoiceItemsTotal() - (sessionDetails?.total || 0)) <= 1
                  ? 'bg-green-50 dark:bg-green-950/30 text-green-700'
                  : 'bg-red-50 dark:bg-red-950/30 text-red-700'
              }`}>
                <span>Total Items:</span>
                <span className="font-bold">${getInvoiceItemsTotal().toLocaleString()}</span>
              </div>
              {Math.abs(getInvoiceItemsTotal() - (sessionDetails?.total || 0)) > 1 && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Debe ser ${(sessionDetails?.total || 0).toLocaleString()}
                </p>
              )}
            </div>

            {/* Client Section */}
            <div className="space-y-2">
              <Label className="font-semibold">Cliente</Label>
              
              <Select value={invoiceDocTipo} onValueChange={(v) => {
                setInvoiceDocTipo(v);
                setInvoiceDocNro('');
                setInvoiceRazonSocial('');
              }}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cf">Consumidor Final</SelectItem>
                  <SelectItem value="cuit">CUIT</SelectItem>
                  <SelectItem value="dni">DNI</SelectItem>
                </SelectContent>
              </Select>

              {invoiceDocTipo !== 'cf' && (
                <div className="flex gap-2">
                  <Input
                    value={invoiceDocNro}
                    onChange={(e) => setInvoiceDocNro(e.target.value.replace(/\D/g, ''))}
                    placeholder={invoiceDocTipo === 'cuit' ? 'CUIT' : 'DNI'}
                    className="flex-1 h-9"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={searchTaxpayer}
                    disabled={searchingTaxpayer || !invoiceDocNro}
                    className="h-9"
                  >
                    {searchingTaxpayer ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
              )}

              {invoiceDocTipo !== 'cf' && invoiceRazonSocial && (
                <div className="flex items-center gap-2 p-2 bg-muted rounded text-sm">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <span>{invoiceRazonSocial}</span>
                </div>
              )}
            </div>

            {/* Info box */}
            <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Punto de Venta:</span>
                <span className="font-mono">{String(afipStatus?.punto_vta || 0).padStart(4, '0')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Emisor:</span>
                <span>{afipStatus?.razon_social || '-'}</span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowInvoiceDialog(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={createInvoice}
              disabled={creatingInvoice || Math.abs(getInvoiceItemsTotal() - (sessionDetails?.total || 0)) > 1}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {creatingInvoice ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Emitiendo...</>
              ) : (
                <><FileText className="w-4 h-4 mr-1" />Emitir Factura</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Result Dialog */}
      <Dialog open={showInvoiceResult} onOpenChange={setShowInvoiceResult}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-6 h-6" />
              ¡Factura Emitida!
            </DialogTitle>
          </DialogHeader>

          {invoiceResult && (
            <div className="space-y-4">
              {/* Número de factura */}
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 rounded-xl">
                <p className="text-sm text-muted-foreground">Factura C</p>
                <p className="text-3xl font-bold text-blue-600">
                  {String(invoiceResult.punto_vta).padStart(4, '0')}-{String(invoiceResult.cbte_nro).padStart(8, '0')}
                </p>
                <p className="text-lg font-semibold mt-2">
                  ${invoiceResult.importe_total?.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                </p>
              </div>

              {/* CAE */}
              <div className="space-y-2 p-3 bg-muted rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">CAE:</span>
                  <span className="font-mono font-medium">{invoiceResult.cae}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Vto. CAE:</span>
                  <span>{invoiceResult.cae_vto?.replace(/(\d{4})(\d{2})(\d{2})/, '$3/$2/$1')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Fecha:</span>
                  <span>{invoiceResult.fecha?.replace(/(\d{4})(\d{2})(\d{2})/, '$3/$2/$1')}</span>
                </div>
              </div>

              {/* QR Code */}
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Código QR Fiscal</p>
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(invoiceResult.qr_url)}`}
                  alt="QR Fiscal"
                  className="mx-auto rounded-lg"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowInvoiceResult(false)}>
              Cerrar
            </Button>
            <Button onClick={printInvoice}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Facturar Mesa Cerrada */}
      <Dialog open={showClosedSessionsDialog} onOpenChange={setShowClosedSessionsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Facturar Mesa Cerrada
            </DialogTitle>
            <DialogDescription>
              Selecciona una mesa cerrada en las últimas 24 horas para emitir factura
            </DialogDescription>
          </DialogHeader>

          {closedSessions.length === 0 ? (
            <div className="py-8 text-center">
              <Receipt className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No hay mesas cerradas sin facturar</p>
              <p className="text-sm text-muted-foreground mt-1">Las mesas cerradas aparecen aquí por 24 horas</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Sessions list */}
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {closedSessions.map((session) => (
                  <div
                    key={session.session_id}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedClosedSession?.session_id === session.session_id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
                        : 'border-border hover:border-blue-300 hover:bg-muted/50'
                    }`}
                    onClick={() => setSelectedClosedSession(session)}
                    data-testid={`closed-session-${session.session_id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{session.unit_name}</p>
                        <p className="text-sm text-muted-foreground">
                          Cerrada: {new Date(session.closed_at).toLocaleString('es-AR')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">${session.total?.toLocaleString()}</p>
                        <Badge variant="outline" className="text-xs">
                          {session.payment_method === 'cash' ? 'Efectivo' : 
                           session.payment_method === 'card' ? 'Tarjeta' : 
                           session.payment_method === 'transfer' ? 'Transferencia' : session.payment_method}
                        </Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Invoice form (shown when session selected) */}
              {selectedClosedSession && (
                <div className="border-t pt-4 space-y-4">
                  <h4 className="font-medium">Datos del receptor</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo Documento</Label>
                      <Select value={invoiceDocTipo} onValueChange={setInvoiceDocTipo}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cf">Consumidor Final</SelectItem>
                          <SelectItem value="dni">DNI</SelectItem>
                          <SelectItem value="cuit">CUIT</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Nro. Documento</Label>
                      <Input
                        placeholder={invoiceDocTipo === 'cf' ? 'No requerido' : 'Número'}
                        value={invoiceDocNro}
                        onChange={(e) => setInvoiceDocNro(e.target.value)}
                        disabled={invoiceDocTipo === 'cf'}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Condición IVA</Label>
                    <Select value={invoiceCondicionIva} onValueChange={setInvoiceCondicionIva}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="consumidor_final">Consumidor Final</SelectItem>
                        <SelectItem value="responsable_inscripto">Responsable Inscripto</SelectItem>
                        <SelectItem value="monotributo">Monotributista</SelectItem>
                        <SelectItem value="exento">Exento</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowClosedSessionsDialog(false);
              setSelectedClosedSession(null);
            }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleInvoiceClosedSession}
              disabled={!selectedClosedSession || invoicingClosedSession}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-invoice-closed-session-btn"
            >
              {invoicingClosedSession ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Emitiendo...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Emitir Factura
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: División por Comensales */}
      <ComensalesSplitDialog
        open={showComensalesSplit}
        onOpenChange={setShowComensalesSplit}
        session={selectedSession}
        onUpdate={fetchSessions}
      />
    </div>
  );
}

export default function CashierViewPage() {
  return useIsModern() ? <ModernCashier /> : <CashierView />;
}
